import React, { useState, useEffect } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EncuestasTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEncuestas();
  }, []);

  const loadEncuestas = async () => {
    try {
      const res = await axios.get(`${API}/encuesta/resultados`);
      setData(res.data);
    } catch (error) {
      console.error("Error loading encuestas:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="encuestas-loading">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const renderStars = (rating) => "⭐".repeat(rating) + "☆".repeat(5 - rating);

  return (
    <div className="space-y-6" data-testid="encuestas-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-white" data-testid="encuestas-total">{data?.total_respuestas || 0}</p>
            <p className="text-xs text-slate-400">Total Respuestas</p>
          </CardContent>
        </Card>

        <Card className="glass border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-400" data-testid="encuestas-promedio">{data?.promedio_calificacion || 0}</p>
            <p className="text-xs text-slate-400">Promedio ⭐</p>
          </CardContent>
        </Card>

        <Card className="glass border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{data?.por_calificacion?.[5] || 0}</p>
            <p className="text-xs text-slate-400">⭐⭐⭐⭐⭐ (5 estrellas)</p>
          </CardContent>
        </Card>

        <Card className="glass border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-400">{(data?.por_calificacion?.[1] || 0) + (data?.por_calificacion?.[2] || 0)}</p>
            <p className="text-xs text-slate-400">⭐⭐ o menos</p>
          </CardContent>
        </Card>
      </div>

      <Card className="glass border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg">Distribución de Calificaciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = data?.por_calificacion?.[star] || 0;
              const total = data?.total_respuestas || 1;
              const percentage = (count / total) * 100;
              return (
                <div key={star} className="flex items-center gap-3">
                  <span className="text-slate-300 w-24">{renderStars(star)}</span>
                  <div className="flex-1 h-4 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        star >= 4 ? 'bg-green-500' : star === 3 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-white font-medium w-12 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="glass border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg">Respuestas Recientes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {data?.encuestas?.length > 0 ? (
              data.encuestas.map((encuesta, idx) => (
                <div key={idx} className="p-4 bg-slate-800/30 rounded-xl">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-white font-medium">{encuesta.nombre || encuesta.email}</p>
                      <p className="text-xs text-slate-500">{encuesta.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-yellow-400">{renderStars(encuesta.calificacion)}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(encuesta.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {encuesta.que_gusto && (
                    <div className="mb-2">
                      <p className="text-xs text-green-400 font-medium">¿Qué le gustó?</p>
                      <p className="text-sm text-slate-300">{encuesta.que_gusto}</p>
                    </div>
                  )}

                  {encuesta.que_mejorar && (
                    <div className="mb-2">
                      <p className="text-xs text-orange-400 font-medium">¿Qué mejorar?</p>
                      <p className="text-sm text-slate-300">{encuesta.que_mejorar}</p>
                    </div>
                  )}

                  {encuesta.mensaje && (
                    <div>
                      <p className="text-xs text-purple-400 font-medium">Mensaje</p>
                      <p className="text-sm text-slate-300">{encuesta.mensaje}</p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-center py-8">No hay encuestas aún</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Button onClick={loadEncuestas} variant="outline" className="w-full" data-testid="encuestas-refresh-btn">
        <Star className="mr-2" size={16} />
        Actualizar Encuestas
      </Button>
    </div>
  );
};

export default EncuestasTab;
